import { useEffect, useId, useMemo, useRef } from 'react'
import { jwtDecode } from 'jwt-decode'
import { useRequest } from 'ahooks'
import { differenceInMilliseconds, parseISO } from 'date-fns'
import { renderSVG } from 'uqr'

import { TagsNoRef } from '@trustycars/genie-ui/types'

import { useModal } from '$contexts/Modal'
import { addToken, DecodedToken } from '$store/session/tokens'
import { asyncSuccessResolver, toast } from '$app/utilities'
import { ToastMessage } from '$app/types'
import { CARRO_ID_APP_QR_SCANNER_ROUTE } from '$constants'

import { AnonymousAuthAPI } from '$services/api/anonymous-auth'
import { AuthAPI } from '$services/api/auth'
import { errorMessageResolver, successResolver } from '$services/api/common'
import { subscribeRtcEvent } from '$services/rtc/auth'

import useCreateAuthRTC from './useCreateAuthRTC'
import { useSuccessLogin } from './useSuccessLogin'

const anonymousAuthApi = new AnonymousAuthAPI(null)

const toastMsg = {
  loading: 'Getting session...',
  success: 'QR code generated'
} satisfies ToastMessage

export const useQrLogin = () => {
  const toastID = useId()

  const { sessionRequest } = anonymousAuthApi

  const lastQrTimeout = useRef<ReturnType<typeof setTimeout>>(undefined)

  const onLoginSuccess = useSuccessLogin()
  const [rtc, createAuthRTC] = useCreateAuthRTC()

  const modal = useModal({
    disabledOverlayClick: true,
    onClose: () => reset()
  })
  const tmpSessionReq = useRequest(
    asyncSuccessResolver(sessionRequest, successResolver),
    {
      manual: true,
      onBefore: () => {
        clearTimeout(lastQrTimeout.current)
      },
      onSuccess: ({ data: { auth_token } }) => {
        const { jti: token_id } = jwtDecode<DecodedToken>(auth_token)

        const authAPI = new AuthAPI(auth_token)

        createAuthRTC({ user_id: 'anonymous-user', authAPI, token_id })
      },
      // Auto refresh QR code when expiration reached
      onFinally: (_, res) => {
        const { expired_at } = { ...res?.data }

        if (!expired_at) return

        const target = parseISO(expired_at)

        const ms = differenceInMilliseconds(target, new Date())

        lastQrTimeout.current = setTimeout(tmpSessionReq.refresh, ms)
      }
    }
  )

  const { runAsync, data: sessionRes, error } = tmpSessionReq

  const requests = [tmpSessionReq]

  const qrContent = useMemo(() => {
    const token = sessionRes?.data.auth_token

    if (!token) return undefined

    const token_id = jwtDecode<DecodedToken>(token).jti

    return renderSVG(`${CARRO_ID_APP_QR_SCANNER_ROUTE}?session_id=${token_id}`)
  }, [sessionRes?.data.auth_token])

  const loading = requests.map(({ loading }) => loading).includes(true)
  const errorMessage = error
    ? errorMessageResolver(error, 'Failed to get session')
    : undefined

  const start = () => {
    modal.modalControls.open()

    toast.promise(runAsync(), {
      ...toastMsg,
      id: toastID
    })
  }

  const cancel = () => {
    modal.modalControls.close()
  }

  const reset = () => {
    clearTimeout(lastQrTimeout.current)

    requests.forEach(({ cancel, mutate }) => {
      cancel()
      mutate()
    })

    toast.dismiss(toastID)
  }

  const buttonProps: TagsNoRef<'button'> = {
    disabled: loading,
    onClick: start
  }

  useEffect(
    () => {
      if (!rtc) return

      const unsub = subscribeRtcEvent('carroid_qr_approved', {
        rtc,
        action: ({ data }) => {
          toast.success('Successfully logged in', { id: toastID })
          onLoginSuccess(addToken(data.auth_token))
        }
      })

      return unsub
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rtc]
  )

  return {
    modal,
    loading,
    buttonProps,
    qrContent,
    errorMessage,
    start,
    cancel
  }
}

export default useQrLogin
